/** MetricsPanel — extracted from panels.jsx. */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { CATEGORIES } from "./_shared";

export function MetricsPanel({ metrics, reload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: CATEGORIES[0], name: "", definition: "", who_target: "", unit: "", order: 0, active: true, age_bands: [] });
  const [open, setOpen] = useState(false);

  const startNew = () => {
    setEditing(null);
    setForm({ category: CATEGORIES[0], name: "", definition: "", who_target: "", unit: "", order: metrics.length, active: true, age_bands: [] });
    setOpen(true);
  };
  const startEdit = (m) => {
    setEditing(m);
    setForm({ ...m, age_bands: m.age_bands || [] });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await http.put(`/metrics/${editing.id}`, form);
      else await http.post("/metrics", form);
      setOpen(false); toast.success("Saved"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this metric?")) return;
    await http.delete(`/metrics/${id}`); toast.success("Deleted"); reload();
  };
  const addBand = () => setForm((f) => ({ ...f, age_bands: [...(f.age_bands || []), { age_min: 3, age_max: 12, gender: "any", target: "" }] }));
  const updBand = (i, patch) => setForm((f) => {
    const arr = [...(f.age_bands || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...f, age_bands: arr };
  });
  const rmBand = (i) => setForm((f) => ({ ...f, age_bands: (f.age_bands || []).filter((_, idx) => idx !== i) }));

  const grouped = useMemo(() => {
    const map = {};
    metrics.forEach((m) => { (map[m.category] = map[m.category] || []).push(m); });
    return Object.entries(map);
  }, [metrics]);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-lg text-slate-900">Configure health parameters</h2>
        <Button data-testid="add-metric-btn" onClick={startNew} className="bg-sky-500 hover:bg-sky-600"><Plus className="w-4 h-4 mr-1" /> New metric</Button>
      </div>
      <div className="space-y-6">
        {grouped.map(([cat, mets]) => (
          <Card key={cat}>
            <CardHeader><CardTitle className="text-sm uppercase tracking-widest text-sky-700 font-bold">{cat}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {mets.map((m) => (
                  <div key={m.id} data-testid={`metric-row-${m.id}`} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800">{m.name} {!m.active && <Badge variant="secondary" className="ml-2">inactive</Badge>}</div>
                      <div className="text-xs text-slate-500 mt-1">WHO: <span className="font-mono text-slate-700">{m.who_target || "—"}</span> {m.unit && <span className="ml-2">· {m.unit}</span>}</div>
                      {(m.age_bands || []).length > 0 && (
                        <div className="text-[10px] text-emerald-700 mt-1 font-medium uppercase tracking-widest">
                          {(m.age_bands || []).length} age-band{(m.age_bands || []).length > 1 ? "s" : ""} configured
                        </div>
                      )}
                      {m.definition && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{m.definition}</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" data-testid={`edit-metric-${m.id}`} onClick={() => startEdit(m)}>Edit</Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-metric-${m.id}`} onClick={() => del(m.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="metric-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit metric" : "New metric"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <select data-testid="metric-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Unit</Label><Input data-testid="metric-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Name</Label><Input data-testid="metric-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Generic WHO Target (fallback)</Label><Input data-testid="metric-who-input" value={form.who_target} onChange={(e) => setForm({ ...form, who_target: e.target.value })} placeholder="Used when no age-band matches" className="mt-1" /></div>
            <div><Label>Definition</Label><Textarea data-testid="metric-definition-input" value={form.definition} onChange={(e) => setForm({ ...form, definition: e.target.value })} className="mt-1" /></div>

            <div className="pt-2 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Age &amp; gender bands</Label>
                  <div className="text-xs text-slate-500">Report card will pick the band matching the child&rsquo;s age &amp; sex.</div>
                </div>
                <Button type="button" size="sm" variant="outline" data-testid="add-age-band-btn" onClick={addBand}>
                  <Plus className="w-3 h-3 mr-1" /> Add band
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {(form.age_bands || []).length === 0 && <div className="text-xs text-slate-400 italic">No bands — will use the generic target above.</div>}
                {(form.age_bands || []).map((b, i) => (
                  <div key={i} data-testid={`age-band-${i}`} className="border border-slate-200 rounded-lg p-3 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400">Age min</Label>
                      <Input type="number" value={b.age_min} onChange={(e) => updBand(i, { age_min: Number(e.target.value) })} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400">Age max</Label>
                      <Input type="number" value={b.age_max} onChange={(e) => updBand(i, { age_max: Number(e.target.value) })} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400">Sex</Label>
                      <select value={b.gender} onChange={(e) => updBand(i, { gender: e.target.value })} className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 bg-white text-sm">
                        <option value="any">Any</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400">Target</Label>
                      <Input value={b.target} onChange={(e) => updBand(i, { target: e.target.value })} placeholder="e.g. 70-110 bpm" className="mt-1 h-9" />
                    </div>
                    <div className="col-span-1 text-right">
                      <Button type="button" size="icon" variant="ghost" data-testid={`remove-band-${i}`} onClick={() => rmBand(i)}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2"><Switch data-testid="metric-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="metric-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
